"use client";

import Image from "next/image";
import Link from "next/link";
import { Phone, ArrowRight, Shield, ExternalLink, Building2, Home, Building, Hotel, HeartPulse, Factory } from "lucide-react";

const F = { display: "'Plus Jakarta Sans', sans-serif", body: '"Barlow", sans-serif', mono: '"DM Mono", monospace' };
const C = { gold: "#D4A84B", white: "#ffffff", w82: "rgba(255,255,255,0.82)", w55: "rgba(255,255,255,0.70)", w30: "rgba(255,255,255,0.30)", navy: "#080F1A", navyMid: "#0d1929" };
const CARD: React.CSSProperties = { background: "rgba(5,13,24,0.60)", backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)", border: "1px solid rgba(255,255,255,0.12)", borderTop: `2px solid ${C.gold}`, borderRadius: 12, padding: "1.5rem", display: "flex", flexDirection: "column" };
const PANEL: React.CSSProperties = { background: "rgba(255,255,255,0.12)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)", border: "1px solid rgba(255,255,255,0.18)", borderRadius: 16, padding: "2.5rem" };
const LABEL: React.CSSProperties = { color: C.gold, fontFamily: F.mono, fontSize: 10, letterSpacing: 3, textTransform: "uppercase", display: "block", marginBottom: "0.6rem" };
const H2: React.CSSProperties = { color: C.white, fontFamily: F.display, fontWeight: 700, fontSize: "clamp(1.8rem, 3.5vw, 3rem)", marginBottom: "0.75rem", lineHeight: 1.1 };
const H3: React.CSSProperties = { color: C.white, fontFamily: F.display, fontWeight: 700, fontSize: "1.1rem", marginBottom: "0.5rem" };
const P: React.CSSProperties = { color: C.w82, fontSize: "0.9rem", lineHeight: 1.65, fontFamily: F.body };
const OVERLAY = (o: number): React.CSSProperties => ({ position: "absolute", inset: 0, background: `rgba(5,13,24,${o})`, zIndex: 1 });
const CONTENT: React.CSSProperties = { position: "relative", zIndex: 2, padding: "5rem 1.5rem", maxWidth: 1200, margin: "0 auto", width: "100%" };
const SEC: React.CSSProperties = { position: "relative", overflow: "hidden", backgroundColor: C.navy };
const GOLDLINE: React.CSSProperties = { width: 48, height: 2, background: C.gold, borderRadius: 2, margin: "16px 0" };
const BTN_RED: React.CSSProperties = { display: "inline-flex", alignItems: "center", gap: 10, background: "linear-gradient(135deg, #DC2626 0%, #dc2626 100%)", color: C.white, fontFamily: F.display, fontWeight: 700, fontSize: "1.1rem", borderRadius: 12, padding: "16px 36px", textDecoration: "none", border: "none", letterSpacing: "1px", boxShadow: "0 8px 32px rgba(220,38,38,0.55), 0 2px 8px rgba(220,38,38,0.4)" };
const BTN_GOLD: React.CSSProperties = { display: "inline-flex", alignItems: "center", gap: 8, background: "transparent", color: C.gold, fontFamily: F.body, fontWeight: 600, fontSize: "1rem", border: `1.5px solid ${C.gold}`, borderRadius: 10, padding: "13px 26px", textDecoration: "none" };

type Assoc = { name: string; url: string; desc: string };
type Vertical = { label: string; icon: typeof Building2; items: Assoc[] };

const verticals: Vertical[] = [
  {
    label: "Construction",
    icon: Building2,
    items: [
      { name: "AZAGC — Arizona Chapter Associated General Contractors", url: "https://azagc.org", desc: "Arizona's oldest commercial construction association, est. 1934." },
      { name: "Arizona Builders' Alliance", url: "https://arizonabuildersalliance.com", desc: "275+ commercial construction firms statewide." },
      { name: "ASA of Arizona — American Subcontractors Association", url: "https://asa-az.org", desc: "Representing specialty and subcontractor trades, est. 1984." },
      { name: "Arizona Construction Association", url: "https://azcontractor.org", desc: "Serving Arizona's construction industry since 1956." },
    ],
  },
  {
    label: "Multifamily",
    icon: Home,
    items: [
      { name: "AMA — Arizona Multihousing Association", url: "https://azmultihousing.org", desc: "2,600+ members, NAA affiliate, representing Arizona's apartment and multifamily industry." },
    ],
  },
  {
    label: "Commercial Property",
    icon: Building,
    items: [
      { name: "BOMA Greater Phoenix", url: "https://bomaphoenix.org", desc: "Building Owners and Managers Association, Greater Phoenix chapter, est. 1936." },
      { name: "IREM Arizona", url: "https://irem.org", desc: "Institute of Real Estate Management, Arizona chapter." },
    ],
  },
  {
    label: "Hospitality",
    icon: Hotel,
    items: [
      { name: "AzLTA — Arizona Lodging & Tourism Association", url: "https://azlta.com", desc: "500+ members, 38,000+ guestrooms statewide." },
      { name: "AzHLA — Arizona Hotel & Lodging Association", url: "https://azhla.com", desc: "400+ members, 35,000+ guestrooms statewide." },
    ],
  },
  {
    label: "Healthcare",
    icon: HeartPulse,
    items: [
      { name: "CASHE — Central Arizona Society for Healthcare Engineering", url: "https://cashe-az.com", desc: "2023 Platinum ASHE Award recipient, serving Phoenix metro healthcare facility engineers." },
      { name: "AZSHE — Arizona Society for Healthcare Engineering", url: "https://azshe.org", desc: "Serving Tucson and Southern Arizona healthcare facilities." },
      { name: "AzHHA — Arizona Hospital and Healthcare Association", url: "https://azhha.org", desc: "Statewide hospital and healthcare advocacy." },
    ],
  },
  {
    label: "Industrial & Technology",
    icon: Factory,
    items: [
      { name: "GPEC — Greater Phoenix Economic Council", url: "https://gpec.org", desc: "195,000+ jobs, $76.5B capital investment, driving Arizona's industrial and technology economy." },
      { name: "Arizona Technology Council", url: "https://aztechcouncil.org", desc: "Arizona's largest technology industry trade association." },
    ],
  },
];

export default function AssociationsPage() {
  return (
    <>
      {/* ═══ HERO ═══ */}
      <section style={{ ...SEC, minHeight: "70vh", display: "flex", alignItems: "center" }}>
        <Image src="/images/arizona-landmarks/antelope-canyon-arizona.jpg" alt="Arizona industry" fill style={{ objectFit: "cover" }} priority />
        <div style={OVERLAY(0.60)} />
        <div style={{ ...CONTENT, maxWidth: 900, textAlign: "center" }}>
          <span style={LABEL}>Resource Directory</span>
          <h1 style={{ color: C.white, fontFamily: F.display, fontWeight: 800, fontSize: "clamp(2rem, 4.5vw, 3.8rem)", lineHeight: 1.05, marginBottom: 20 }}>
            Arizona Industry Associations.<br />Every Vertical. One Page.
          </h1>
          <div style={{ ...GOLDLINE, margin: "20px auto" }} />
          <p style={{ ...P, fontSize: "1.1rem", maxWidth: 720, margin: "0 auto" }}>
            A reference directory for Arizona property professionals — construction, multifamily, commercial,
            hospitality, healthcare, and industrial. Bookmark this page.
          </p>
        </div>
      </section>

      {/* ═══ DIRECTORY SECTIONS ═══ */}
      <section style={{ ...SEC, backgroundColor: C.navyMid }}>
        <div style={CONTENT}>
          {verticals.map((v) => {
            const Icon = v.icon;
            return (
              <div key={v.label} style={{ marginBottom: "3.5rem" }}>
                {/* Section header */}
                <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: "1.5rem", paddingBottom: "1rem", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                  <Icon size={28} style={{ color: C.gold, flexShrink: 0 }} />
                  <div>
                    <span style={LABEL}>Vertical</span>
                    <h2 style={{ ...H2, fontSize: "clamp(1.5rem, 3vw, 2.2rem)", marginBottom: 0 }}>{v.label}</h2>
                  </div>
                </div>

                {/* Cards grid */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "1rem" }}>
                  {v.items.map((a) => (
                    <a key={a.url} href={a.url} target="_blank" rel="noopener noreferrer" style={{ ...CARD, textDecoration: "none" }}>
                      <h3 style={H3}>{a.name}</h3>
                      <p style={{ ...P, flex: 1, marginBottom: 12 }}>{a.desc}</p>
                      <span style={{ color: C.gold, fontSize: "0.82rem", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6, fontFamily: F.body }}>
                        {a.url.replace("https://", "")} <ExternalLink size={12} />
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ═══ DRR CONTEXT CALLOUT ═══ */}
      <section style={{ ...SEC, padding: "60px 24px" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <div style={{ ...PANEL, padding: "2.5rem" }}>
            <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
              <Shield size={32} style={{ color: C.gold, flexShrink: 0, marginTop: 4 }} />
              <div style={{ flex: 1, minWidth: 260 }}>
                <span style={{ ...LABEL, marginBottom: 10 }}>DRR In Context</span>
                <h3 style={{ ...H2, fontSize: "clamp(1.3rem, 2.5vw, 1.8rem)", marginBottom: 16 }}>
                  DRR Serves Every Property Vertical Listed Here
                </h3>
                <p style={{ ...P, marginBottom: 20 }}>
                  When a water, fire, or environmental loss hits any of these facilities, we respond 24/7.
                  The same enterprise-grade process — applied to every property type, every time.
                </p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 20 }}>
                  {["AZ ROC #349012", "IICRC Certified", "ICRA 2.0", "HAZWOPER"].map((t) => (
                    <span key={t} style={{ background: "rgba(212,168,75,0.12)", border: "1px solid rgba(212,168,75,0.28)", borderRadius: 20, padding: "4px 14px", fontFamily: F.mono, fontSize: "9px", letterSpacing: "1.5px", textTransform: "uppercase", color: C.gold, fontWeight: 600 }}>
                      {t}
                    </span>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                  <Link href="/who-we-help/commercial" style={{ color: C.gold, fontSize: "0.82rem", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6, textDecoration: "none" }}>
                    Commercial Property <ArrowRight size={12} />
                  </Link>
                  <Link href="/who-we-help/healthcare" style={{ color: C.gold, fontSize: "0.82rem", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6, textDecoration: "none" }}>
                    Healthcare <ArrowRight size={12} />
                  </Link>
                  <Link href="/who-we-help/multifamily" style={{ color: C.gold, fontSize: "0.82rem", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6, textDecoration: "none" }}>
                    Multifamily <ArrowRight size={12} />
                  </Link>
                  <Link href="/who-we-help/industrial" style={{ color: C.gold, fontSize: "0.82rem", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6, textDecoration: "none" }}>
                    Industrial <ArrowRight size={12} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ CTA ═══ */}
      <section style={{ ...SEC, minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Image src="/images/arizona-landmarks/monument-valley-night-arizona.jpg" alt="Arizona night" fill style={{ objectFit: "cover" }} />
        <div style={OVERLAY(0.55)} />
        <div style={{ ...CONTENT, maxWidth: 750, textAlign: "center" }}>
          <div style={{ ...PANEL, padding: "56px 40px" }}>
            <span style={{ ...LABEL, marginBottom: 20 }}>AVAILABLE 24 · 7 · 365</span>
            <h2 style={{ color: C.white, fontFamily: F.display, fontWeight: 800, fontSize: "clamp(1.8rem, 4vw, 3rem)", lineHeight: 1.08, marginBottom: 16 }}>
              Serving every industry<br />on this page.
            </h2>
            <p style={{ ...P, fontSize: "1rem", marginBottom: 36, maxWidth: 520, margin: "0 auto 36px" }}>
              From day one to the day the project is completed. One call, one team, every property vertical in Arizona.
            </p>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
              <Link href="/contact" style={BTN_GOLD}>
                Request Vendor Consultation <ArrowRight size={16} />
              </Link>
            </div>
            <p style={{ marginTop: 28, fontFamily: F.mono, fontSize: "0.62rem", letterSpacing: "2px", textTransform: "uppercase", color: C.w30 }}>
              AZ ROC #349012 · IICRC · ICRA 2.0 · HAZWOPER
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
